import React, { useEffect, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { assignmentsApi, learningPathsApi } from '../../api/client';

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysRemaining(value?: string | null) {
  if (!value) return null;
  return Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);
}

function isDone(assignment: any) {
  return !!(assignment?.isTrainingComplete || assignment?.status === 'completed');
}

function statusColor(status?: string) {
  if (status === 'completed') return 'success' as const;
  if (status === 'in_progress') return 'warning' as const;
  return 'default' as const;
}

const LearningPathProgressPage: React.FC = () => {
  const [paths, setPaths] = useState<any[]>([]);
  const [pathAssignments, setPathAssignments] = useState<any[]>([]);
  const [courseAssignments, setCourseAssignments] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pathFilter, setPathFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [pathRows, assignmentRows, courseRows] = await Promise.all([
          learningPathsApi.list(),
          learningPathsApi.listAssignments(),
          assignmentsApi.list(),
        ]);
        setPaths(pathRows);
        setPathAssignments(assignmentRows);
        setCourseAssignments(courseRows);
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = useMemo(() => {
    return paths
      .map((path) => {
        const assigned = pathAssignments.filter((item) => item.learningPathId === path.id);
        if (!assigned.length) return null;
        const required = (path.courses || []).filter((course: any) => course.isRequired !== false);
        const employees = assigned.map((assignment) => {
          const mine = courseAssignments.filter((item) => item.employeeId === assignment.employeeId && (item.learningPathId === path.id || required.some((course: any) => course.courseId === item.courseId)));
          const coursesCompleted = required.filter((course: any) => mine.some((item) => item.courseId === course.courseId && isDone(item))).length;
          const totalCourses = required.length;
          const progressPercentage = totalCourses ? Math.round((coursesCompleted / totalCourses) * 100) : (assignment.status === 'completed' ? 100 : 0);
          return { ...assignment, coursesCompleted, totalCourses, progressPercentage };
        });
        const completedCount = employees.filter((item) => item.progressPercentage === 100 || item.status === 'completed').length;
        const startedCount = employees.filter((item) => item.coursesCompleted > 0 || item.status === 'in_progress' || item.status === 'completed').length;
        return {
          path,
          courseCount: required.length,
          employees,
          assignedCount: employees.length,
          completedCount,
          startedCount,
          completionPercentage: employees.length ? Math.round((completedCount / employees.length) * 100) : 0,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => b.assignedCount - a.assignedCount) as any[];
  }, [paths, pathAssignments, courseAssignments]);

  const filtered = groups.filter((group) => {
    if (pathFilter && group.path.id !== pathFilter) return false;
    if (statusFilter === 'completed' && group.completedCount === 0) return false;
    if (statusFilter === 'in_progress' && group.startedCount === 0) return false;
    if (statusFilter === 'not_started' && group.startedCount > 0) return false;
    return true;
  });

  const openDetail = (group: any, assignment: any) => {
    const required = (group.path.courses || []).filter((course: any) => course.isRequired !== false);
    const mine = courseAssignments.filter((item) => item.employeeId === assignment.employeeId);
    const courses = required.map((course: any) => {
      const match = mine.find((item) => item.courseId === course.courseId && (item.learningPathId === group.path.id || !item.learningPathId || item.learningPathId === group.path.id));
      return { title: course.course?.title || 'Course', done: isDone(match), required: course.isRequired !== false };
    });
    setDetail({
      employeeName: assignment.employee?.name || 'Employee',
      email: assignment.employee?.email,
      pathTitle: group.path.title,
      status: assignment.status || 'not_started',
      assignedDate: assignment.assignedDate || assignment.createdAt,
      dueDate: assignment.dueDate,
      coursesCompleted: assignment.coursesCompleted,
      totalCourses: assignment.totalCourses,
      courses,
    });
  };

  const exportCsv = () => {
    const lines = ['Learning Path,Employee,Email,Status,Progress,Courses Completed,Total Courses,Assigned,Due Date'];
    filtered.forEach((group) => {
      group.employees.forEach((employee: any) => {
        lines.push([
          group.path.title,
          employee.employee?.name,
          employee.employee?.email,
          employee.status,
          `${employee.progressPercentage}%`,
          employee.coursesCompleted,
          employee.totalCourses,
          formatDate(employee.assignedDate),
          formatDate(employee.dueDate),
        ].map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','));
      });
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Learning_Path_Progress.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const popup = window.open('', '_blank');
    if (!popup) return;
    const rows = filtered.flatMap((group) => group.employees.map((employee: any) => `<tr><td>${group.path.title}</td><td>${employee.employee?.name || ''}</td><td>${employee.status || ''}</td><td>${employee.progressPercentage}%</td></tr>`)).join('');
    popup.document.write(`<!DOCTYPE html><html><head><title>Learning Path Progress</title><style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left}</style></head><body><h1>Learning Path Progress Report</h1><table><thead><tr><th>Learning Path</th><th>Employee</th><th>Status</th><th>Progress</th></tr></thead><tbody>${rows}</tbody></table><script>window.print()</script></body></html>`);
    popup.document.close();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>Learning Path Progress</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv}>Export CSV</Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportPdf}>Export PDF</Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Path</InputLabel>
              <Select value={pathFilter} label="Filter by Path" onChange={(e) => setPathFilter(e.target.value)}>
                <MenuItem value="">All Paths</MenuItem>
                {paths.map((path) => <MenuItem key={path.id} value={path.id}>{path.title}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select value={statusFilter} label="Filter by Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="completed">Has Completed</MenuItem>
                <MenuItem value="in_progress">Has In Progress</MenuItem>
                <MenuItem value="not_started">None Started</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No learning paths with assignments found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Learning Path</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Version</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Courses</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}><GroupIcon fontSize="small" /> Employees</Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((group) => {
                const open = expandedId === group.path.id;
                const sequential = group.path.isSequential !== false;
                return (
                  <React.Fragment key={group.path.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedId(open ? null : group.path.id)}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                          <Typography fontWeight={600}>{group.path.title}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center"><Chip label={`v${group.path.version || 1}`} size="small" variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <Chip label={sequential ? 'Sequential' : 'Flexible'} size="small" color={sequential ? 'info' : 'default'} variant={sequential ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell align="center">{group.courseCount}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Chip icon={<GroupIcon />} label={group.assignedCount} color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">({group.completedCount} done)</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Box sx={{ width: 80 }}>
                            <LinearProgress variant="determinate" value={group.completionPercentage} sx={{ height: 8, borderRadius: 4 }} color={group.completionPercentage === 100 ? 'success' : 'primary'} />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 40 }}>{group.completionPercentage}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} unmountOnExit>
                          <Box sx={{ py: 2, px: 4 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Assigned Employees ({group.employees.length})</Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {['Employee', 'Email', 'Status', 'Progress', 'Assigned', 'Due Date', 'Expiration', 'Certification'].map((column) => (
                                    <TableCell key={column} align={['Status', 'Progress', 'Certification'].includes(column) ? 'center' : 'left'}>{column}</TableCell>
                                  ))}
                                  <TableCell align="center">Details</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.employees.map((employee: any) => {
                                  const dueDays = daysRemaining(employee.dueDate);
                                  const expired = employee.expirationDate && new Date(employee.expirationDate).getTime() < Date.now();
                                  return (
                                    <TableRow key={employee.id}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {employee.progressPercentage === 100 ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                                          {employee.employee?.name || 'Unknown'}
                                        </Box>
                                      </TableCell>
                                      <TableCell>{employee.employee?.email || '—'}</TableCell>
                                      <TableCell align="center">
                                        <Chip label={(employee.status || 'not_started').replace('_', ' ')} size="small" color={statusColor(employee.status)} />
                                      </TableCell>
                                      <TableCell align="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                          <Box sx={{ width: 60 }}>
                                            <LinearProgress variant="determinate" value={employee.progressPercentage} sx={{ height: 6, borderRadius: 3 }} color={employee.progressPercentage === 100 ? 'success' : 'primary'} />
                                          </Box>
                                          <Typography variant="caption">{employee.coursesCompleted}/{employee.totalCourses}</Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>{formatDate(employee.assignedDate || employee.createdAt)}</TableCell>
                                      <TableCell>
                                        {employee.dueDate ? (
                                          <Box>
                                            <Typography variant="body2">{formatDate(employee.dueDate)}</Typography>
                                            {dueDays !== null && employee.status !== 'completed' && (
                                              <Typography variant="caption" color={dueDays < 0 ? 'error' : dueDays <= 3 ? 'warning.main' : 'text.secondary'}>
                                                {dueDays < 0 ? `${Math.abs(dueDays)}d overdue` : `${dueDays}d left`}
                                              </Typography>
                                            )}
                                          </Box>
                                        ) : '—'}
                                      </TableCell>
                                      <TableCell>{formatDate(employee.expirationDate)}</TableCell>
                                      <TableCell align="center">
                                        {employee.expirationDate ? (
                                          <Chip label={expired ? 'Expired' : 'Valid'} size="small" color={expired ? 'error' : 'success'} />
                                        ) : '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Tooltip title="View details">
                                          <IconButton size="small" color="primary" onClick={() => openDetail(group, employee)}>
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Employee Progress: {detail?.employeeName}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>{detail?.pathTitle}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`Status: ${(detail?.status || '').replace('_', ' ')}`} color={statusColor(detail?.status)} size="small" />
            <Chip label={`Progress: ${detail?.coursesCompleted || 0} / ${detail?.totalCourses || 0} courses`} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">Assigned: {formatDate(detail?.assignedDate)}</Typography>
          {detail?.dueDate && <Typography variant="body2" color="text.secondary">Due: {formatDate(detail.dueDate)}</Typography>}
          <Typography fontWeight={700} sx={{ mt: 2, mb: 1 }}>Courses</Typography>
          {(detail?.courses || []).map((course: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
              <Typography>{index + 1}. {course.title}</Typography>
              <Chip label={course.done ? 'Completed' : 'Pending'} size="small" color={course.done ? 'success' : 'default'} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearningPathProgressPage;
